<template>
  <div
    class="container transition"
    :style="containerColorStyle"
  >
    <div
      ref="section-0"
      class="p-4 section"
    >
      <h1>ZeMo-Test</h1>
      <h3>Finde deine Zeitmonster</h3>
      <p>Welche der folgenden Aussagen trifft auf dich zu?</p>
      <div class="d-flex">
        <img
          style="height:15em"
          src="@/assets/fragezeichen.png"
        >
        <p class="pl-2">
          Kreuze alle zutreffenden Aussagen an.<br> Benutze den "Weiter"-Knopf,
          das Mausrad oder die Navigation rechts, um zur nächsten Seite zu gelangen
        </p>
      </div>
      <p>
        <button
          class="btn mt-3 btn-outline-dark"
          @click="scrollToSection(selection+1)"
        >
          Weiter
        </button>
      </p>
    </div>
    <div
      v-for="tab in pageCount-2"
      :key="tab"
      :ref="`section-${tab}`"
      class="p-4 section"
    >
      <h1 class="mt-5">
        Fragebogen #{{ tab }}
      </h1>
      <div
        v-for="question in questionsPerPage"
        :key="question"
      >
        <div
          v-if="questions[question + (tab-1) * questionsPerPage - 1]"
          class="form-check py-3"
        >
          <input
            id="defaultCheck1"
            v-model="answers[question + (tab-1) * questionsPerPage - 1]"
            class="form-check-input"
            type="checkbox"
          >
          <label
            class="form-check-label"
            for="defaultCheck1"
          >
            {{ questions[question + (tab-1) * questionsPerPage - 1].text }}
          </label>
        </div>
      </div>
      <button
        class="btn mt-3 btn-outline-dark"
        @click="scrollToSection(selection+1)"
      >
        {{ tab === pageCount-2 ? 'Weiter zum Ergebnis' : 'Weiter' }}
      </button>
    </div>
    <div
      :ref="
        `section-${pageCount-1}`"
      class="p-4 section"
      style="overflow:auto"
    >
      <h1>Du bist fertig!</h1>
      <div v-if="results.length === 0">
        Du hast keine Fragen beantwortet, daher konnten wir für dich kein Ergebniss berechnen.
      </div>
      <h4 v-else>
        Deine Zeitfresser sind:
      </h4>
      <div
        v-for="(result, index) in results"
        :key="index"
        class="pl-3"
      >
        <h5>{{ Math.floor(result.percentage) }}% {{ result.caption }}</h5>
        <img
          style="width:12em;float:left;border-right: 0.4em solid transparent;"
          :src="result.image"
        >
        <p>Merkmale:</p>
        <ul>
          <li
            v-for="behaviour in result.behaviours"
            :key="behaviour"
          >
            {{ behaviour }}
          </li>
        </ul>
        <p>Mögliche Maßnahmen:</p>
        <ul>
          <li
            v-for="solution in result.solutions"
            :key="solution"
          >
            {{ solution }}
          </li>
        </ul>
      </div>
      <a
        target="_blank"
        href="../../../downloads/zemo_test_v2.pdf"
      >Download PDF</a>
    </div>
    <scroll-menu
      :selection="selection"
      :pages="pages"
      @scrollTo="scrollToSection"
    />
  </div>
</template>

<script>
import ScrollMenu from '@/components/sections/ScrollMenu.vue';
import { questions, resultOptions } from '@/data/questions';

export default {
  components: {
    ScrollMenu,
  },
  data() {
    return {
      selection: 0,
      questionsPerPage: 4,
      inMove: false,
      answers: [],
      questions,
    };
  },
  computed: {
    pageCount() {
      return Math.ceil(questions.length / this.questionsPerPage) + 2; // 4 questions per page
    },
    results() {
      // 30% caption
      // 20% caption

      const result = {};
      let voteAmount = 0;

      questions.forEach((q, i) => {
        if (this.answers[i]) {
          q.results.forEach((r) => {
            if (!result[r]) {
              result[r] = resultOptions[r];
              result[r].votes = 0;
            }
            voteAmount += 1;
            result[r].votes += 1;
          });
        }
      });

      const array = Object.values(result);
      array.sort((a, b) => b.votes - a.votes);
      return array.map((a) => ({ ...a, percentage: (a.votes / voteAmount) * 100 }));
    },
    pages() {
      const result = [];
      result.push({ caption: 'index', color: '#1E5959' });
      for (let i = 1; i <= Math.ceil(questions.length / this.questionsPerPage); i += 1) {
        result.push({ caption: `questions #${i}`, color: '#945E31' });
      }
      result.push({ caption: 'result', color: '#943131' });
      return result;
    },

    containerColorStyle() {
      if (this.selection === 0) {
        return {
          color: '#012B2B',
          'background-color': '#2E6969',
        };
      } if (this.selection === this.pageCount - 1) {
        return {
          color: '#470202',
          'background-color': '#943131',
        };
      }
      return {
        color: '#472102',
        'background-color': '#945E31',
      };
    },
  },

  // we add event listeners for user scroll behaviour for different browsers
  created() {
    window.addEventListener('DOMMouseScroll', this.handleMouseWheelDOM); // Mozilla Firefox
    window.addEventListener('mousewheel', this.handleMouseWheel, {
      passive: false,
    }); // Other browsers
    window.addEventListener('touchstart', this.touchStart, {
      passive: false,
    }); // mobile devices
    window.addEventListener('touchmove', this.touchMove, {
      passive: false,
    });

    if (this.$route.hash) {
      const hashIndex = parseInt(this.$route.hash.replace('#', ''), 10);
      if (hashIndex >= 0) {
        this.selection = hashIndex;
        this.$nextTick(() => {
          this.scrollToSection(hashIndex);
        });
      }
    }
  },
  // we remove the event listeners when this component is removed
  destroyed() {
    window.removeEventListener('mousewheel', this.handleMouseWheel, {
      passive: false,
    }); // Other browsers
    window.removeEventListener('DOMMouseScroll', this.handleMouseWheelDOM); // Mozilla Firefox
    window.removeEventListener('touchstart', this.touchStart); // mobile devices
    window.removeEventListener('touchmove', this.touchMove); // mobile devices
  },
  methods: {
    scrollToSection(id) {
      // we set a boolean here to avoid double scrolls. We unset it after .4 seconds
      // and we only scroll if we have a change in the selection
      if (!this.inMove && this.selection !== id) {
        this.inMove = true;
        setTimeout(() => { this.inMove = false; }, 400);

        // we update our selection variable
        this.selection = id;

        // we scroll to our new selected tab
        const section = Array.isArray(this.$refs[`section-${id}`]) ? this.$refs[`section-${id}`][0] : this.$refs[`section-${id}`];
        section.scrollIntoView({
          behavior: 'smooth',
        });

        // if its not the first tab we update the url #
        if (id > 0) {
          this.$router.push({ hash: `#${id}` });
        } else {
          this.$router.push({ hash: undefined });
        }
      }
    },
    handleMouseWheel(e) {
      // check if we scroll up or down
      if (e.wheelDelta < 30 && !this.inMove) {
        this.moveDown();
      } else if (e.wheelDelta > 30 && !this.inMove) {
        this.moveUp();
      }
      // prevent default cancels the default behaviour of scrolling
      e.preventDefault();
      return false;
    },
    moveUp() {
      // move up if we are not at pos 0
      if (this.selection > 0) {
        this.scrollToSection(this.selection - 1, true);
      }
    },
    moveDown() {
      // move down if we are not at the end
      if (this.selection < this.pageCount - 1) {
        this.scrollToSection(this.selection + 1, true);
      }
    },
  },
};
</script>


<style scoped>
.section {
  height:100vh;
}
.transition {
  transition: all 0.2s linear;
}

ul { overflow: hidden; }
</style>
